import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { User } from "../types/user";
import { tokenService } from "../services/tokenService";
import { getApiUrl } from "../config/api";

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, name: string) => Promise<void>;
  logout: () => void;
  clearStorage: () => void;
  checkToken: () => { token: string | null; user: string | null };
  updateUser: (userData: Partial<User>) => void;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Проверяем токен в localStorage при загрузке
    const token = tokenService.getAccessToken();

    if (token) {
      // Загружаем данные из localStorage для быстрого отображения
      const userData = localStorage.getItem("user");
      if (userData) {
        const parsedUser = JSON.parse(userData);
        // Добавляем значения по умолчанию для новых полей
        const userWithDefaults = {
          ...parsedUser,
          role: parsedUser.role || "user",
          // Wildberries API Configuration
          wildberriesApiKey: parsedUser.wildberriesApiKey || "",
          // OZON API Configuration
          ozonApiKey: parsedUser.ozonApiKey || "",
          ozonClientId: parsedUser.ozonClientId || "",
          ozonClientSecret: parsedUser.ozonClientSecret || "",
          // Yandex.Market API Configuration
          yandexApiKey: parsedUser.yandexApiKey || "",
          yandexClientId: parsedUser.yandexClientId || "",
          yandexClientSecret: parsedUser.yandexClientSecret || "",
          yandexCampaignId: parsedUser.yandexCampaignId || "",
          yandexOAuthToken: parsedUser.yandexOAuthToken || "",
          emailNotifications: parsedUser.emailNotifications ?? true,
          negativeReviewNotifications:
            parsedUser.negativeReviewNotifications ?? true,
          globalPromptNote: parsedUser.globalPromptNote || "",
        };
        setUser(userWithDefaults);
      }

      // Загружаем актуальные данные с сервера
      fetchUserProfile();
    }
    setIsLoading(false);
  }, []);

  const fetchUserProfile = async () => {
    try {
      const response = await tokenService.makeAuthenticatedRequest(
        getApiUrl("/api/users/profile")
      );

      if (response.ok) {
        const userData = await response.json();
        const userWithDefaults = {
          ...userData,
          role: userData.role || "user",
          // Wildberries API Configuration
          wildberriesApiKey: userData.wildberriesApiKey || "",
          // OZON API Configuration
          ozonApiKey: userData.ozonApiKey || "",
          ozonClientId: userData.ozonClientId || "",
          ozonClientSecret: userData.ozonClientSecret || "",
          // Yandex.Market API Configuration
          yandexApiKey: userData.yandexApiKey || "",
          yandexClientId: userData.yandexClientId || "",
          yandexClientSecret: userData.yandexClientSecret || "",
          yandexCampaignId: userData.yandexCampaignId || "",
          yandexOAuthToken: userData.yandexOAuthToken || "",
          emailNotifications: userData.emailNotifications ?? true,
          negativeReviewNotifications:
            userData.negativeReviewNotifications ?? true,
          autoGeneration: userData.autoGeneration ?? false,
          globalPromptNote: userData.globalPromptNote || "",
        };
        setUser(userWithDefaults);
        localStorage.setItem("user", JSON.stringify(userWithDefaults));
      }
    } catch (error) {
      console.error("Ошибка при загрузке профиля:", error);
      // Если не удалось загрузить профиль, очищаем токены
      tokenService.clearTokens();
      setUser(null);
    }
  };

  const login = async (email: string, password: string) => {
    try {
      const response = await fetch(getApiUrl("/api/auth/login"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("AuthContext - Login error response:", errorText);
        throw new Error("Ошибка входа");
      }

      const data = await response.json();

      const userData = {
        id: data.user.id,
        email: data.user.email,
        role: data.user.role,
        companyName: data.user.companyName,
        isActive: data.user.isActive,
        // Wildberries API Configuration
        wildberriesApiKey: data.user.wildberriesApiKey,
        // OZON API Configuration
        ozonApiKey: data.user.ozonApiKey,
        ozonClientId: data.user.ozonClientId,
        ozonClientSecret: data.user.ozonClientSecret,

        // Yandex.Market API Configuration
        yandexApiKey: data.user.yandexApiKey,
        yandexClientId: data.user.yandexClientId,
        yandexClientSecret: data.user.yandexClientSecret,
        yandexCampaignId: data.user.yandexCampaignId,
        yandexOAuthToken: data.user.yandexOAuthToken,
        emailNotifications: data.user.emailNotifications ?? true,
        negativeReviewNotifications:
          data.user.negativeReviewNotifications ?? true,
        autoGeneration: data.user.autoGeneration ?? false,
        globalPromptNote: data.user.globalPromptNote || "",
        createdAt: data.user.createdAt,
        updatedAt: data.user.updatedAt,
      };

      // Сохраняем оба токена
      tokenService.setTokens(data.access_token, data.refresh_token);
      localStorage.setItem("user", JSON.stringify(userData));
      setUser(userData);
    } catch (error) {
      console.error("AuthContext - Ошибка входа:", error);
      throw error;
    }
  };

  const register = async (email: string, password: string, name: string) => {
    try {
      const response = await fetch(getApiUrl("/api/auth/register"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, password, name }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("AuthContext - Register error response:", errorText);
        throw new Error("Ошибка регистрации");
      }

      const data = await response.json();

      const userData = {
        id: data.user.id,
        email: data.user.email,
        role: data.user.role,
        companyName: data.user.companyName,
        isActive: data.user.isActive,
        // Wildberries API Configuration
        wildberriesApiKey: data.user.wildberriesApiKey,
        // OZON API Configuration
        ozonApiKey: data.user.ozonApiKey,
        ozonClientId: data.user.ozonClientId,
        ozonClientSecret: data.user.ozonClientSecret,

        // Yandex.Market API Configuration
        yandexApiKey: data.user.yandexApiKey,
        yandexClientId: data.user.yandexClientId,
        yandexClientSecret: data.user.yandexClientSecret,
        yandexCampaignId: data.user.yandexCampaignId,
        yandexOAuthToken: data.user.yandexOAuthToken,
        emailNotifications: data.user.emailNotifications ?? true,
        negativeReviewNotifications:
          data.user.negativeReviewNotifications ?? true,
        autoGeneration: data.user.autoGeneration ?? false,
        globalPromptNote: data.user.globalPromptNote || "",
        createdAt: data.user.createdAt,
        updatedAt: data.user.updatedAt,
      };

      // Сохраняем оба токена
      tokenService.setTokens(data.access_token, data.refresh_token);
      localStorage.setItem("user", JSON.stringify(userData));
      setUser(userData);
    } catch (error) {
      console.error("AuthContext - Ошибка регистрации:", error);
      throw error;
    }
  };

  const logout = () => {
    tokenService.clearTokens();
    localStorage.removeItem("user");
    setUser(null);
  };

  const clearStorage = () => {
    tokenService.clearTokens();
    localStorage.clear();
    setUser(null);
  };

  const checkToken = () => {
    const token = tokenService.getAccessToken();
    const user = localStorage.getItem("user");
    return { token, user };
  };

  const updateUser = (userData: Partial<User>) => {
    if (user) {
      const updatedUser = { ...user, ...userData };
      setUser(updatedUser);
      localStorage.setItem("user", JSON.stringify(updatedUser));
    }
  };

  const value = {
    user,
    login,
    register,
    logout,
    clearStorage,
    checkToken,
    updateUser,
    isLoading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
